import { iconButtonClasses, type IconButtonProps } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { COLORS_SCHEMA } from '../../palette';

// NEW VARIANT
declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'soft' | 'contained' | 'outlined' | 'text';
    square?: boolean;
  }
  interface IconButtonPropsColorOverrides {
    default: true;
  }
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
  interface IconButtonPropsSizeOverrides {
    xsmall: true;
    small: true;
    medium: true;
    large: true;
    xlarge: true;
  }
}

export default function IconButton(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyles = (ownerState: IconButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';
    if (!ownerState.color) ownerState.color = 'default';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const xSmallSize = ownerState.size === 'xsmall';

    const smallSize = ownerState.size === 'small';

    const mediumSize = ownerState.size === 'medium';

    const largeSize = ownerState.size === 'large';

    const xLargeSize = ownerState.size === 'xlarge';

    const isSquare = Boolean(ownerState.square);

    const defaultStyle = {
      borderRadius: isSquare ? 0 : theme.spacing(0.5),
    };

    const disabledState = {
      [`&.${iconButtonClasses.disabled}`]: {
        // CONTAINED
        ...(containedVariant && {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.grey[400],
          border: `1px solid ${theme.palette.grey[400]}`,
        }),
        // OUTLINED
        ...(outlinedVariant && {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.grey[400],
          border: `1px solid ${theme.palette.grey[300]}`,
        }),
        // TEXT
        ...(textVariant && {
          backgroundColor: 'transparent',
          color: theme.palette.grey[400],
          border: 'none',
          '& > .Icon': {
            color: theme.palette.grey[400],
          },
        }),
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[400],
          '& > .Icon': {
            color: theme.palette.grey[400],
          },
        }),

        [`& > svg`]: {
          color: theme.palette.grey[400],
        },
      },
    };

    const size = {
      '& > .Icon': {
        height: 24,
        width: 24,
        minWidth: 24,
        ' > svg': {
          height: 24,
          width: 24,
          minWidth: 24,
        },
      },
      ...(xSmallSize && {
        height: 24,
        width: 24,
        padding: 4,
        '& > .Icon': {
          height: 16,
          width: 16,
          minWidth: 16,
          ' > svg': {
            height: 16,
            width: 16,
            minWidth: 16,
          },
        },
      }),
      ...(smallSize && {
        height: 32,
        width: 32,
        padding: 4,
        '& > .Icon': {
          height: 22,
          width: 22,
          minWidth: 22,
          ' > svg': {
            height: 22,
            width: 22,
            minWidth: 22,
          },
        },
      }),
      ...(mediumSize && {
        height: 40,
        width: 40,
        padding: 8,
      }),
      ...(largeSize && {
        height: 48,
        width: 48,
        padding: 12,
      }),
      ...(xLargeSize && {
        height: 56,
        width: 56,
        padding: 16,
      }),
    };

    const colorStyle = Object.values(COLORS_SCHEMA).reduce((acc, color) => {
      if (ownerState.color === color) {
        return {
          ...acc,
          // CONTAINED
          ...(containedVariant && {
            backgroundColor: theme.palette[color][500],
            color: theme.palette[color].contrastText,
            border: `1px solid ${theme.palette[color][500]}`,
            '&:hover': {
              backgroundColor: theme.palette[color][600],
              border: `1px solid ${theme.palette[color][600]}`,
            },
            '&:focus': {
              border: `1px solid ${theme.palette[color][400]}`,
              backgroundColor: theme.palette[color][400],
              color:
                color === 'default'
                  ? theme.palette.common.white
                  : theme.palette[color].contrastText,
              '& > .Icon': {
                color:
                  color === 'default'
                    ? theme.palette.common.white
                    : theme.palette[color].contrastText,
              },
            },
          }),
          // OUTLINED
          ...(outlinedVariant && {
            backgroundColor: theme.palette.common.white,
            color: theme.palette[color].main,
            border: `1px solid ${
              color === 'default'
                ? theme.palette.grey[400]
                : theme.palette[color][500]
            }`,
            '& > .Icon': {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][500],
            },
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
              '& > .Icon': {
                color: theme.palette[color][600],
              },
            },
            '&:focus': {
              borderColor: theme.palette[color][400],
              '& > .Icon': {
                color:
                  color === 'default'
                    ? theme.palette.grey[700]
                    : theme.palette[color][400],
              },
            },
          }),
          // TEXT
          ...(textVariant && {
            backgroundColor: 'transparent',
            color:
              color === 'default'
                ? theme.palette.grey[700]
                : theme.palette[color][500],
            border: `none`,
            '& > .Icon': {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][500],
            },
            '&:hover': {
              backgroundColor: theme.palette[color][50],
              '& > .Icon': {
                color:
                  color === 'default'
                    ? theme.palette.grey[700]
                    : theme.palette[color][600],
              },
            },
            '&:focus': {
              '& > .Icon': {
                color:
                  color === 'default'
                    ? theme.palette.grey[700]
                    : theme.palette[color][400],
              },
            },
          }),
          // SOFT
          ...(softVariant && {
            backgroundColor: theme.palette[color][100],
            color: theme.palette[color][600],
            border: `none`,

            '& > .Icon': {
              color: theme.palette[color][600],
            },

            '&:hover': {
              backgroundColor: theme.palette[color][200],
            },
            '&:focus': {
              backgroundColor: theme.palette[color][300],
            },
          }),
        };
      }
      return acc;
    }, {});

    return { ...defaultStyle, ...disabledState, ...size, ...colorStyle };
  };

  return {
    MuiIconButton: {
      defaultProps: {
        color: 'default',
        variant: 'text',
        disableElevation: true,
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles(ownerState),
        // Add variant-specific overrides
        colorPrimary: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles({ ...ownerState, color: 'primary' }),
        colorSecondary: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles({ ...ownerState, color: 'secondary' }),
        colorError: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles({ ...ownerState, color: 'error' }),
        colorWarning: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles({ ...ownerState, color: 'warning' }),
        colorInfo: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles({ ...ownerState, color: 'info' }),
        colorSuccess: ({ ownerState }: { ownerState: IconButtonProps }) =>
          rootStyles({ ...ownerState, color: 'success' }),
      },
    },
  };
}
